// src/features/dashboard/components/EmployeeLoggedHoursReport/TodayTimeSplit.tsx

import { useState } from 'react';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';

import {
  TimeSplitResponse,
  LoggedHoursKpiResponse,
} from '../../types/Employee/employeeLoggedHours.types';

interface Props {
  data?: TimeSplitResponse[];
  selectedDate?: Date | string | null;
  kpi?: LoggedHoursKpiResponse | null;
}

interface ChartItem extends TimeSplitResponse {
  name: string;
  value: number;
  percentage: number;
  durationMinutes: number;
  color: string;
}

const COLORS: Record<string, string> = {
  Active: '#29C45A',
  Meeting: '#4A86E8',
  Idle: '#F3A41C',
};

const ALLOWED_CATEGORIES = [
  'Active',
  'Meeting',
  'Idle',
];

/* ==========================================================================
   HELPERS
   ========================================================================== */

/**
 * Check selected date is today.
 */
const isToday = (
  selectedDate?: Date | string | null,
): boolean => {
  if (!selectedDate) {
    return false;
  }

// Returns true only when the selected date matches today's date.
// Invalid dates return false.
  const date = new Date(selectedDate);

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  const today = new Date();

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

/**
 * Convert loggedToday to minutes.
 *
 * Supports:
 *
 * "01h 56m" -> 116
 * "1h 56m"  -> 116
 * "01:56:00" -> 116
 * "01:56"    -> 116
 */
const loggedTimeToMinutes = (
  value?: string | number | null,
): number => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 0;
  }

  /* ----------------------------------------------------------
     Numeric value
     ---------------------------------------------------------- */

  if (typeof value === 'number') {
    return Math.max(
      0,
      Math.round(value),
    );
  }

  const text = String(value).trim();

  if (!text) {
    return 0;
  }

  /* ----------------------------------------------------------
     HHh MMm
     Example: 01h 56m
     ---------------------------------------------------------- */

  const hourMinuteMatch =
    text.match(
      /(\d+(?:\.\d+)?)\s*h\s*(\d+)?\s*m?/i,
    );

  if (hourMinuteMatch) {
    const hours =
      Number(hourMinuteMatch[1]) || 0;

    const minutes =
      Number(hourMinuteMatch[2]) || 0;

    return Math.max(
      0,
      Math.round(
        hours * 60 + minutes,
      ),
    );
  }

  /* ----------------------------------------------------------
     HH:MM:SS
     ---------------------------------------------------------- */

  const timeMatch =
    text.match(
      /^(\d+):(\d{1,2})(?::(\d{1,2}))?$/,
    );

  if (timeMatch) {
    const hours =
      Number(timeMatch[1]) || 0;

    const minutes =
      Number(timeMatch[2]) || 0;

    const seconds =
      Number(timeMatch[3]) || 0;

    return Math.max(
      0,
      Math.round(
        hours * 60 +
        minutes +
        seconds / 60,
      ),
    );
  }

  return 0;
};

/**
 * Format minutes for UI.
 *
 * 116 -> 1h 56m
 */
const formatDuration = (
  totalMinutes: number,
): string => {
  const minutes = Math.max(
    0,
    Math.round(
      Number(totalMinutes) || 0,
    ),
  );

  const hours =
    Math.floor(minutes / 60);

  const remainingMinutes =
    minutes % 60;

  return `${hours}h ${remainingMinutes}m`;
};

/* ==========================================================================
   PIE LABEL
   ========================================================================== */

const renderLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: any) => {
  if (
    percent === undefined ||
    percent === null
  ) {
    return null;
  }

  const RADIAN =
    Math.PI / 180;

  const radius =
    innerRadius +
    (outerRadius - innerRadius) *
      0.55;

  const x =
    cx +
    radius *
      Math.cos(
        -midAngle * RADIAN,
      );

  const y =
    cy +
    radius *
      Math.sin(
        -midAngle * RADIAN,
      );

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={700}
      pointerEvents="none"
    >
      {`${Math.round(
        percent * 100,
      )}%`}
    </text>
  );
};

/* ==========================================================================
   COMPONENT
   ========================================================================== */

const TodayTimeSplit = ({
  data = [],
  selectedDate,
  kpi,
}: Props) => {
  const [
    activeIndex,
    setActiveIndex,
  ] = useState<number | null>(
    null,
  );

  /* ------------------------------------------------------------------------
     LOGGED HOURS FROM KPI
     ------------------------------------------------------------------------ */

  /**
   * KPI example:
   *
   * loggedToday: "01h 56m"
   *
   * 01h 56m
   *      ↓
   * 116 minutes
   */
  const totalLoggedMinutes =
    loggedTimeToMinutes(
      kpi?.loggedToday,
    );

  const loggedTime =
    formatDuration(
      totalLoggedMinutes,
    );

  /* ------------------------------------------------------------------------
     HEADER
     ------------------------------------------------------------------------ */

  const heading = isToday(
    selectedDate,
  )
    ? "Today's Time Split"
    : 'Time Split';

  /* ------------------------------------------------------------------------
     FILTER CATEGORIES
     ------------------------------------------------------------------------ */

  const filteredData =
    data.filter((item) =>
      ALLOWED_CATEGORIES.includes(
        item.category,
      ),
    );

  /* ------------------------------------------------------------------------
     TOTAL PERCENTAGE
     ------------------------------------------------------------------------ */

  const totalPercentage =
    filteredData.reduce(
      (sum, item) =>
        sum +
        Number(
          item.percentage || 0,
        ),
      0,
    );

  /* ------------------------------------------------------------------------
     CHART DATA
     ------------------------------------------------------------------------ */

  const chartData: ChartItem[] =
    filteredData.map((item) => {
      const apiPercentage =
        Number(
          item.percentage || 0,
        );

      /*
       * Normalize API percentages
       * so donut always totals 100%.
       */

      const percentage =
        totalPercentage > 0
          ? (
              apiPercentage /
              totalPercentage
            ) * 100
          : 0;

      /*
       * Category duration is based on
       * the REAL KPI logged time.
       *
       * Example:
       *
       * KPI Logged = 116 minutes
       * Active = 54%
       *
       * 116 × 54% = 63 minutes
       */

      const durationMinutes =
        Math.round(
          (totalLoggedMinutes *
            percentage) /
            100,
        );

      return {
        ...item,

        name: item.category,

        value: percentage,

        percentage,

        durationMinutes,

        color:
          COLORS[item.category] ??
          '#AAB4C4',
      };
    });

  /* ------------------------------------------------------------------------
     TARGET
     ------------------------------------------------------------------------ */

  const TARGET_MINUTES =
    9 * 60;

  const targetTime =
    formatDuration(
      TARGET_MINUTES,
    );

  const targetPercentage =
    Math.min(
      100,
      Math.round(
        (totalLoggedMinutes /
          TARGET_MINUTES) *
          100,
      ),
    );

  /* ------------------------------------------------------------------------
     HOVERED ITEM
     ------------------------------------------------------------------------ */

  const activeItem =
    activeIndex !== null
      ? chartData[activeIndex]
      : undefined;

  /* ------------------------------------------------------------------------
     UI
     ------------------------------------------------------------------------ */

  return (
    <div
      className="
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ==================================================================
          HEADER
      ================================================================== */}

      <div
        className="
          mb-3
          flex
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          {heading}
        </h3>

        <span
          className="
            whitespace-nowrap
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          How {loggedTime} was distributed
        </span>
      </div>

      {/* ==================================================================
          DONUT
      ================================================================== */}

      <div
        className="
          relative
          mx-auto
          h-[220px]
          w-[220px]
        "
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={52}
              outerRadius={86}
              stroke="#FFFFFF"
              strokeWidth={1}
              paddingAngle={0}
              labelLine={false}
              label={renderLabel}
              isAnimationActive={false}
              onMouseEnter={(
                _entry,
                index,
              ) => {
                setActiveIndex(
                  index,
                );
              }}
              onMouseLeave={() => {
                setActiveIndex(
                  null,
                );
              }}
            >
              {chartData.map(
                (
                  item,
                  index,
                ) => {
                  const opacity =
                    activeIndex ===
                      null ||
                    activeIndex ===
                      index
                      ? 1
                      : 0.3;

                  return (
                    <Cell
                      key={`${item.name}-${index}`}
                      fill={item.color}
                      fillOpacity={
                        opacity
                      }
                      stroke="#FFFFFF"
                      strokeWidth={1}
                      style={{
                        cursor:
                          'pointer',
                        outline:
                          'none',
                      }}
                    />
                  );
                },
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* ================================================================
            CENTER
        ================================================================ */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            flex
            h-[90px]
            w-[90px]
            -translate-x-1/2
            -translate-y-1/2
            flex-col
            items-center
            justify-center
            text-center
          "
        >
          {activeItem ? (
            <>
              <span
                className="
                  text-[12px]
                  font-semibold
                "
                style={{
                  color:
                    activeItem.color,
                }}
              >
                {activeItem.name}
              </span>

              <span
                className="
                  mt-1
                  text-[18px]
                  font-bold
                  leading-none
                  text-[#101828]
                "
              >
                {Math.round(
                  activeItem.percentage,
                )}
                %
              </span>

              <span
                className="
                  mt-1
                  text-[9px]
                  text-[#667085]
                "
              >
                {
                  activeItem.sessionCount
                }{' '}
                sessions
              </span>
            </>
          ) : (
            <>
              <span
                className="
                  text-[11px]
                  text-[#667085]
                "
              >
                Logged
              </span>

              <span
                className="
                  mt-1
                  text-[18px]
                  font-semibold
                  leading-tight
                  text-[#101828]
                "
              >
                {loggedTime}
              </span>

              <span
                className="
                  mt-1
                  text-[9px]
                  text-[#667085]
                "
              >
                Target {targetTime}
              </span>
            </>
          )}
        </div>
      </div>

      {/* ==================================================================
          LEGEND
      ================================================================== */}

      <div
        className="
          mt-3
          flex
          flex-wrap
          items-center
          justify-center
          gap-4
          text-[12px]
          text-[#344054]
        "
      >
        {chartData.map(
          (
            item,
            index,
          ) => {
            const isActive =
              activeIndex ===
                null ||
              activeIndex ===
                index;

            return (
              <div
                key={item.name}
                className="
                  flex
                  cursor-pointer
                  items-center
                  gap-1
                "
                onMouseEnter={() =>
                  setActiveIndex(
                    index,
                  )
                }
                onMouseLeave={() =>
                  setActiveIndex(
                    null,
                  )
                }
              >
                <span
                  className="
                    h-3
                    w-3
                    rounded-full
                  "
                  style={{
                    backgroundColor:
                      item.color,
                    opacity:
                      isActive
                        ? 1
                        : 0.3,
                  }}
                />

                <span
                  style={{
                    opacity:
                      isActive
                        ? 1
                        : 0.4,
                  }}
                >
                  {item.name}
                </span>

                <span
                  className="
                    font-medium
                  "
                  style={{
                    opacity:
                      isActive
                        ? 1
                        : 0.4,
                  }}
                >
                  {Math.round(
                    item.percentage,
                  )}
                  %
                </span>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default TodayTimeSplit;